// components/TelegramChatWindowAdmin.jsx
//
// Panel de soporte del ADMIN: lista los usuarios que escribieron al chat y
// permite responderles.
//
// OJO con tres cosas que estaban rotas y que hacen que el panel no funcione:
//  1) Los fetch iban SIN header Authorization: authorize responde 401 con un
//     objeto JSON y ese objeto terminaba en setUsers(), con lo que
//     users.map(...) reventaba con "users.map is not a function".
//  2) La sala era la del ADMIN (user_<id del admin>) en vez de la del usuario
//     seleccionado: los mensajes del usuario nunca llegaban al panel.
//  3) El token del localStorage viene con JSON.stringify (va entrecomillado),
//     así que hay que parsearlo antes de mandarlo.
//
// OJO: la lista de usuarios y el historial se cargan por HTTP (no por socket),
// así que el panel funciona aunque el socket esté caído. El socket solo suma
// la actualización en vivo.
import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { AuthContext } from '../context/authContext';
import { normalizarEstado, ETIQUETA_ESTADO, TEXTO_ESTADO, claseBordeEstado } from '../utils/estadoMensaje';
import { etiquetaAdminRemitente } from '../utils/remitenteMensaje';
import { useChatSoporte } from '../context/ChatSoporteContext';

const SALA_ADMIN_SOPORTE = 'admin_soporte';
// Con socket conectado no se hace polling: los eventos mantenienen la lista y
// la conversación. Sin socket se refresca cada minuto como red de seguridad.
const INTERVALO_RESPALDO_MS = 60000;
// La conversación se marca leída un rato después de abrirla, para que el borde
// de los mensajes pendientes se vea.
const DEMORA_MARCAR_LEIDOS_MS = 5000;

const tokenDeStorage = () => {
	try {
		const raw = localStorage.getItem('token');
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
};

export default function ChatWindow({ role, user, userId, socket, isSocketConnected }) {
	const auth = useContext(AuthContext);
	const token = auth?.token || tokenDeStorage();
	// Nombre del admin logueado: se muestra en el encabezado para que, con
	// varios admins usando el mismo panel, se sepa quién está atendiendo.
	const nombreAdmin = (typeof user === 'string' && user.trim()) || auth?.user || '';
	// Contador de la cola compartido con el menú Admin y el botón flotante.
	const { cargarCola } = useChatSoporte();

	const [users, setUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [messages, setMessages] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [replyText, setReplyText] = useState('');
	const [errorMsg, setErrorMsg] = useState('');
	// Aviso de la última respuesta enviada (si el usuario estaba o no
	// conectado cuando se le respondió).
	// true = se envió una respuesta a esta conversación. El texto NO se guarda:
	// se deriva del estado de presencia actual (en_conversacion / en_linea), así
	// se actualiza solo si el usuario cierra el chat o cierra sesión.
	const [respuestaEnviada, setRespuestaEnviada] = useState(false);
	// Indicador de "escribiendo": userId de quien está tipeando ahora.
	const [escribiendo, setEscribiendo] = useState({});
	const messagesEndRef = useRef(null);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	// ------------------------------------------------------------------
	// Carga de datos
	// ------------------------------------------------------------------
	const loadUsers = useCallback(async () => {
		try {
			const res = await fetch('/api/users', {
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!res.ok) {
				const detalle = await res.json().catch(() => ({}));
				setErrorMsg(detalle?.error || `No se pudo cargar la lista de usuarios (${res.status})`);
				return;
			}

			const data = await res.json();
			// Si el backend devolviera algo que no es un array, no lo metemos en
			// el estado: el .map de abajo reventaría (el bug original).
			const lista = Array.isArray(data) ? data : [];
			setUsers(lista);
			setErrorMsg('');
			// La conversación abierta se refresca con la lista: el objeto
			// seleccionado era un "snapshot" de la carga anterior, así que el
			// punto verde del encabezado se quedaba CONGELADO (pasaba cuando
			// el usuario cerraba la pestaña: el server limpiaba el registro
			// y el header seguía diciendo "En el chat").
			setSelectedUser((sel) =>
				sel ? (lista.find((u) => String(u.user_id) === String(sel.user_id)) || null) : null
			);
			// La presencia (en_linea) la manda el backend: se recalcula en cada
			// carga, así que el punto verde se apaga solo cuando el usuario se
			// desconecta.
		} catch (err) {
			console.error('Error cargando usuarios:', err);
			setErrorMsg('No se pudo cargar la lista de usuarios');
		}
	}, [token]);

	const loadMessages = useCallback(async (idUsuario) => {
		if (!idUsuario) return;
		try {
			const res = await fetch(`/api/messages/${idUsuario}`, {
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!res.ok) {
				const detalle = await res.json().catch(() => ({}));
				setErrorMsg(detalle?.error || `No se pudo cargar la conversación (${res.status})`);
				setIsLoading(false);
				return;
			}

			const data = await res.json();
			setMessages(Array.isArray(data) ? data : []);
			setIsLoading(false);
			setErrorMsg('');
		} catch (err) {
			console.error('Error cargando mensajes:', err);
			setErrorMsg('No se pudo cargar la conversación');
			setIsLoading(false);
		}
	}, [token]);

	// ------------------------------------------------------------------
	// Efectos
	// ------------------------------------------------------------------
	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	useEffect(() => {
		loadUsers();
	}, [loadUsers]);

	// Al abrir una conversación, sus mensajes salen de la cola (el usuario ve
	// que los leídos).
	const marcarLeidos = useCallback(async (idUsuario) => {
		if (!idUsuario) return;
		try {
			const res = await fetch('/api/chat/marcar-leidos', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify({ userId: idUsuario }),
			});
			if (!res.ok) return;
			// El estado local también pasa a 'leido': el servidor emite el evento
			// solo a la sala del usuario, así que el admin no se enteraría y el
			// borde de "sin leer" quedaría pegado en la conversación abierta.
			setMessages((prev) => prev.map((m) =>
				(m.from_sender === 'user' && m.estado !== 'leido') ? { ...m, estado: 'leido' } : m
			));
			await loadUsers();
			// El badge de la cola (menú Admin y botón flotante) tiene que bajar
			// en el acto, no en el próximo poll.
			cargarCola();
		} catch (err) {
			console.error('Error marcando los mensajes como leídos:', err);
		}
	}, [token, loadUsers, cargarCola]);

	// Se depende del ID y no del objeto: loadUsers reemplaza selectedUser por un
	// objeto nuevo de la lista (misma conversación), y con la identidad el efecto
	// se volvía a disparar en cada refresco.
	const selectedUserId = selectedUser?.user_id;

	useEffect(() => {
		setRespuestaEnviada(false); // el aviso es de la conversación anterior
		if (selectedUserId) {
			loadMessages(selectedUserId);
		} else {
			setMessages([]);
		}
	}, [selectedUserId, loadMessages]);

	// Marcar leído con una demora: si se marca en el acto, el borde de los
	// mensajes pendientes desaparece antes de que el admin lo vea.
	useEffect(() => {
		if (!selectedUserId) return undefined;
		const timer = setTimeout(() => { marcarLeidos(selectedUserId); }, DEMORA_MARCAR_LEIDOS_MS);
		return () => clearTimeout(timer);
	}, [selectedUserId, marcarLeidos]);

	// El admin se une a la sala del panel: recibe los mensajes de cualquier
	// usuario, tenga o no una conversación abierta.
	useEffect(() => {
		if (!socket) return;
		socket.emit('joinRoom', SALA_ADMIN_SOPORTE);
	}, [socket]);

	// Al elegir un usuario, se une a la sala de ESE usuario (el server valida
	// que sea admin para poder meterse en una sala ajena).
	useEffect(() => {
		if (!socket || !selectedUser) return;
		socket.emit('joinRoom', `user_${selectedUser.user_id}`);
	}, [socket, selectedUser]);

	// Mensajes en vivo
	useEffect(() => {
		if (!socket) return;

		const handleNewMessage = (message) => {
			if (!message) return;

			setMessages((prev) => {
				if (!selectedUserId || String(message.user_id) !== String(selectedUserId)) {
					return prev;
				}
				// Dedupe: el mismo mensaje puede llegar por el socket y por el
				// refresco por HTTP.
				if (message.id && prev.some((m) => m.id === message.id)) return prev;
				return [...prev, message];
			});

			// La lista de usuarios muestra el último mensaje: hay que refrescarla.
			loadUsers();

			// Si llega un mensaje del usuario con ESTA conversación abierta, el
			// admin lo está viendo: se marca leído en el acto para que el borde
			// de "sin leer" no quede pegado y el contador baje solo.
			const esDelUsuario = message.from_sender !== 'admin' && message.sender !== 'admin';
			if (esDelUsuario && selectedUserId && String(message.user_id) === String(selectedUserId)) {
				marcarLeidos(selectedUserId);
			}
		};

		// El server entrega la cola cuando se conecta un admin.
		const handleCola = () => {
			loadUsers();
		};

		// El usuario abrió el chat: sus respuestas pendientes pasan a 'leido'.
		// Además de refrescar la lista (contador "sin leer"), hay que aplicar el
		// cambio a los mensajes abiertos: si no, la leyenda de los mensajes del
		// admin se quedaba en "Entregado" aunque el usuario ya los hubiera leído.
		const handleEstadoMensajes = (payload) => {
			const ids = new Set((payload?.ids || []).map(Number));
			if (ids.size) {
				setMessages((prev) => prev.map((m) =>
					(ids.has(Number(m.id)) ? { ...m, estado: payload.estado } : m)
				));
			}
			loadUsers();
		};

		// Alguien abrió o cerró el chat de soporte: cambia el punto verde de
		// la lista (el usuario entró a la conversación o se fue).
		const handlePresenciaUsuarios = () => {
			loadUsers();
		};

		// Indicador de "escribiendo" (tipo WhatsApp).
		const handleEscribiendo = (payload) => {
			if (!payload?.userId) return;
			const id = String(payload.userId);

			setEscribiendo((prev) => {
				if (!payload.escribiendo) {
					if (!prev[id]) return prev;
					const { [id]: _quitado, ...resto } = prev;
					return resto;
				}
				return { ...prev, [id]: payload.userName || 'Usuario' };
			});
		};

		// Reconexión del socket: los eventos que pasaron mientras estaba caído
		// no llegan solos, así que se resincroniza una vez.
		const handleConectar = () => {
			loadUsers();
			if (selectedUserId) loadMessages(selectedUserId);
		};

		socket.on('new-message', handleNewMessage);
		socket.on('chat_cola', handleCola);
		socket.on('chat_escribiendo', handleEscribiendo);
		socket.on('chat_mensajes_estado', handleEstadoMensajes);
		socket.on('chat_presencia_usuarios', handlePresenciaUsuarios);
		socket.on('connect', handleConectar);

		return () => {
			socket.off('new-message', handleNewMessage);
			socket.off('chat_cola', handleCola);
			socket.off('chat_escribiendo', handleEscribiendo);
			socket.off('chat_mensajes_estado', handleEstadoMensajes);
			socket.off('chat_presencia_usuarios', handlePresenciaUsuarios);
			socket.off('connect', handleConectar);
		};
	}, [socket, selectedUserId, loadUsers, loadMessages, marcarLeidos]);

	// El indicador se apaga solo: si el usuario deja de tipear, el server
	// avisa (y si se cae la conexión, el estado local expira igual).
	useEffect(() => {
		const ids = Object.keys(escribiendo);
		if (!ids.length) return;

		const timer = setTimeout(() => {
			setEscribiendo((prev) => {
				const copia = { ...prev };
				ids.forEach((id) => delete copia[id]);
				return copia;
			});
		}, 6000);

		return () => clearTimeout(timer);
	}, [escribiendo]);

	// Respaldo SOLO sin socket: con el socket conectado la lista y la
	// conversación se mantienen con los eventos. Se depende del id y no del
	// objeto para no recrear el intervalo en cada refresco de la lista.
	const selectedUserIdRespaldo = selectedUser?.user_id;

	useEffect(() => {
		const timer = setInterval(() => {
			if (!socket?.connected) {
				loadUsers();
				if (selectedUserIdRespaldo) loadMessages(selectedUserIdRespaldo);
			}
		}, INTERVALO_RESPALDO_MS);

		return () => clearInterval(timer);
	}, [socket, loadUsers, loadMessages, selectedUserIdRespaldo]);

	// ------------------------------------------------------------------
	// Envío de la respuesta
	// ------------------------------------------------------------------
	const handleSendReply = async () => {
		if (!selectedUser || !replyText.trim()) return;

		const texto = replyText.trim();
		const payload = {
			userId: selectedUser.user_id,
			message: texto,
		};

		try {
			const res = await fetch('/api/messages/reply', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify(payload),
			});

			if (!res.ok) {
				const detalle = await res.json().catch(() => ({}));
				setErrorMsg(detalle?.error || 'No se pudo enviar el mensaje');
				return;
			}

			const data = await res.json();
			// El backend devuelve el mensaje guardado: se usa ese (tiene id y
			// timestamp reales). Si no viniera, se arma uno local.
			const nuevo = data?.message || {
				user_id: selectedUser.user_id,
				from_sender: 'admin',
				message: texto,
				timestamp: new Date().toISOString(),
			};

			setMessages((prev) => {
				if (nuevo.id && prev.some((m) => m.id === nuevo.id)) return prev;
				return [...prev, nuevo];
			});
			setReplyText('');

			// El admin tiene que saber si el usuario la va a ver AHORA o si se
			// queda guardada. El mensaje NO se pierde en ningún caso: lo que
			// cambia es cuándo lo ve. El texto se arma en el render a partir de
			// la presencia actual, así no queda desactualizado si el usuario
			// cierra el chat o cierra sesión después de enviar.
			setRespuestaEnviada(true);
			loadUsers();
		} catch (err) {
			console.error('Error al enviar la respuesta:', err);
			setErrorMsg('No se pudo enviar el mensaje');
		}
	};

	const formatTime = (timestamp) => {
		if (!timestamp) return '';
		return new Date(timestamp).toLocaleTimeString('es-ES', {
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	// Total de mensajes sin leer (la cola del admin).
	const totalEnCola = users.reduce((total, u) => total + (Number(u.sin_leer) || 0), 0);

	// Leyenda posterior al envío, derivada de la presencia ACTUAL del usuario.
	// Si cierra el chat o la sesión, cambia sola (via loadUsers/presencia) en
	// vez de quedar con el texto que tenía al momento de enviar.
	const avisoEnvio = !respuestaEnviada
		? ''
		: selectedUser?.en_conversacion
			? 'Respuesta enviada: la está viendo ahora.'
			: selectedUser?.en_linea
				? 'Respuesta enviada: tiene la app abierta, la ve cuando abra el chat.'
				: 'El usuario está desconectado: la respuesta quedó guardada y la va a ver cuando entre.';

	// ------------------------------------------------------------------
	// Indicador de "escribiendo"
	// ------------------------------------------------------------------
	// Se avisa al usuario con throttling (uno cada 2 segundos como máximo) para
	// no inundar el socket con un evento por tecla.
	const ultimoAvisoRef = useRef(0);
	const timerAvisoRef = useRef(null);

	const avisarEscribiendo = useCallback(() => {
		if (!socket || !selectedUser) return;

		const ahora = Date.now();
		if (ahora - ultimoAvisoRef.current < 2000) return;
		ultimoAvisoRef.current = ahora;

		socket.emit('chat_escribiendo', { userId: selectedUser.user_id });

		// Si deja de escribir, se avisa para apagar el indicador del otro.
		if (timerAvisoRef.current) clearTimeout(timerAvisoRef.current);
		timerAvisoRef.current = setTimeout(() => {
			socket.emit('chat_dejo_de_escribir');
		}, 3000);
	}, [socket, selectedUser]);

	// Al cambiar de conversación, se apaga el indicador de la anterior.
	useEffect(() => {
		return () => {
			if (timerAvisoRef.current) clearTimeout(timerAvisoRef.current);
			socket?.emit('chat_dejo_de_escribir');
		};
	}, [socket, selectedUser]);

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------
	return (
		<div className="flex flex-col h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
			{/* Header */}
			<div className="bg-white dark:bg-gray-800 shadow px-6 py-4">
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-baseline gap-2 min-w-0">
						<h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Panel de Soporte</h1>
						{nombreAdmin && (
							<span className="text-lg font-medium text-gray-500 dark:text-gray-400 truncate">
								— {nombreAdmin}
							</span>
						)}
					</div>
					{/* Cola: mensajes de usuarios que todavía no se abrieron. */}
					{totalEnCola > 0 ? (
						<span className="text-xs font-medium px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
							{totalEnCola === 1 ? '1 mensaje en cola' : `${totalEnCola} mensajes en cola`}
						</span>
					) : (
						<span className="text-xs text-gray-400 dark:text-gray-500">Cola vacía</span>
					)}
				</div>
			</div>

			{errorMsg && (
				<div className="px-6 py-2 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm">
					{errorMsg}
				</div>
			)}

			<div className="flex flex-1 overflow-hidden">
				{/* Lista de usuarios */}
				<div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">Usuarios</h2>
					</div>
					<div className="flex-1 overflow-y-auto">
						{users.length === 0 ? (
							<p className="text-center text-gray-500 dark:text-gray-400 p-4">No hay mensajes</p>
						) : (
							users.map((u) => (
								<div
									key={u.user_id}
									onClick={() => setSelectedUser(u)}
									className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedUser?.user_id === u.user_id ? 'bg-blue-50 dark:bg-gray-700 border-l-4 border-l-blue-500' : ''
										}`}
								>
									<div className="flex items-center justify-between gap-2">
										<p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
											{/* Presencia REAL: el punto verde es solo si el
											    usuario tiene el CHAT DE SOPORTE abierto ahora.
											    Tener la app abierta no es lo mismo: si cerró
											    el chat, la respuesta se le queda esperando y
											    no puede figurar como conectado. */}
											<span
												className={`w-2 h-2 rounded-full shrink-0 ${u.en_conversacion ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
												title={u.en_conversacion ? 'En el chat' : 'No está en el chat'}
											/>
											<span className="truncate">{u.user_name || `Usuario ${u.user_id}`}</span>
											{escribiendo[String(u.user_id)] && (
												<span className="text-xs font-normal italic text-blue-600 dark:text-blue-400">
													escribiendo...
												</span>
											)}
										</p>
										{Number(u.sin_leer) > 0 && (
											<span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
												{u.sin_leer}
											</span>
										)}
									</div>
									<p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-1">{u.message}</p>
									<p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
										{formatTime(u.timestamp)}
										{!u.en_conversacion && <span className="ml-2">· no está en el chat</span>}
										{/* Respuestas del admin que el usuario todavía no leyó: la
										    garantía de entrega se cierra acá (si el usuario
										    estaba desconectado, la respuesta quedó
										    esperando). */}
										{Number(u.respuestas_sin_leer) > 0 && (
											<span className="ml-2 text-amber-600 dark:text-amber-400">
												· {u.respuestas_sin_leer} sin leer
											</span>
										)}
									</p>
								</div>
							))
						)}
					</div>
				</div>

				{/* Chat activo */}
				<div className="flex-1 flex flex-col">
					{selectedUser ? (
						<>
							{/* Chat Header */}
							<div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
								<div className="flex items-center justify-between gap-2">
									<h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
										<span
											className={`w-2.5 h-2.5 rounded-full shrink-0 ${selectedUser.en_conversacion ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
											title={selectedUser.en_conversacion ? 'En el chat' : 'No está en el chat'}
										/>
										Chat con {selectedUser.user_name || selectedUser.user_id}
									</h3>
									{escribiendo[String(selectedUser.user_id)] && (
										<span className="text-xs italic text-blue-600 dark:text-blue-400">
											{escribiendo[String(selectedUser.user_id)]} está escribiendo...
										</span>
									)}
								</div>
								{/* Presencia + estado de la última respuesta: el admin
								    tiene que saber si el usuario la va a ver ahora o
								    cuando abra el chat. */}
								<div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
									{selectedUser.en_conversacion ? (
										<span className="text-green-600 dark:text-green-400">En el chat: la ve ahora</span>
									) : (
										<span className="text-amber-600 dark:text-amber-400">
											No está en el chat: la respuesta queda guardada y la ve cuando abra el chat
										</span>
									)}
									{Number(selectedUser.respuestas_sin_leer) > 0 && (
										<span className="ml-2 text-amber-600 dark:text-amber-400">
											· {selectedUser.respuestas_sin_leer} respuesta(s) sin leer
										</span>
									)}
								</div>
							</div>

							{/* Mensajes */}
							<div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-100 dark:bg-gray-800">
								{isLoading ? (
									<p className="text-center text-gray-500 dark:text-gray-400">Cargando mensajes...</p>
								) : messages.length === 0 ? (
									<p className="text-center text-gray-500 dark:text-gray-400">No hay mensajes aún</p>
								) : (
									messages.map((message, i) => {
										// from_sender 'admin' = lo escribió el admin (derecha).
										const esAdmin = message.from_sender === 'admin' || message.sender === 'admin';
										// Estado de entrega (colores y leyenda compartidos con el chat
										// del usuario). El admin ve la leyenda de SUS mensajes.
										const estado = normalizarEstado(message.estado);
										const etiqueta = ETIQUETA_ESTADO[estado];
										const claseEstado = TEXTO_ESTADO[estado];
										// Mensaje del usuario que el admin todavía no leyó: en cola
										// punteado, entregado sólido (leído no lleva borde).
										const claseSinLeer = esAdmin ? '' : claseBordeEstado(estado);
										return (
											<div key={message.id ?? i} className={`flex ${esAdmin ? 'justify-end' : 'justify-start'}`}>
												<div
													className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow-sm ${esAdmin
														? 'bg-blue-500 text-white rounded-br-none'
														: 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-bl-none'
														} ${claseSinLeer}`}
												>
													{esAdmin && (
														<p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5 opacity-80">
															{etiquetaAdminRemitente(message)}
														</p>
													)}
													<p className="text-sm break-words">{message.message}</p>
													<div className="text-xs mt-1 flex items-center gap-2 opacity-90">
														<span>{formatTime(message.timestamp)}</span>
														{esAdmin && <span className={claseEstado}>{etiqueta}</span>}
													</div>
												</div>
											</div>
										);
									})
								)}
								<div ref={messagesEndRef} />
							</div>

							{/* Input */}
							<div className="bg-white dark:bg-gray-800 p-4 border-t border-gray-200 dark:border-gray-700">
								<div className="flex gap-2">
									<textarea
										value={replyText}
										onChange={(e) => {
											setReplyText(e.target.value);
											// Avisa que está escribiendo (para que el usuario lo vea).
											// Con throttling: no se manda un evento por tecla.
											avisarEscribiendo();
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && !e.shiftKey) {
												e.preventDefault();
												handleSendReply();
											}
										}}
										placeholder="Escribe una respuesta..."
										rows="1"
										className="flex-1 resize-none border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>
									<button
										disabled={!replyText.trim()}
										onClick={handleSendReply}
										className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Enviar
									</button>
								</div>
								{/* El socket es solo para la actualización en vivo: el envío
								    va por HTTP y funciona igual sin conexión. */}
								<p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
									{isSocketConnected ? 'En vivo por socket' : 'Sin socket: se refresca cada 10 segundos'}
								</p>
								{avisoEnvio && (
									<p className="text-xs mt-1 text-blue-600 dark:text-blue-400">{avisoEnvio}</p>
								)}
							</div>
						</>
					) : (
						<div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
							<p className="text-gray-500 dark:text-gray-400">Selecciona un usuario para comenzar el chat</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
